/**
 * ثبت ضایعات — مستقل از مراحل دیگر و فقط برای مغایرت‌گیری.
 * با «کد بیجک / کد تولید» به بیجک برش وصل می‌شود.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AutoComplete, Button, Card, Col, Input, InputNumber, Popconfirm, Row, Select, Space,
  Statistic, Table, Tag, Typography, message,
} from "antd";
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  createWasteEvent, deleteWasteEvent, getCutBijacks, getEmployees, getWasteEvents,
} from "../../api/client";
import { JalaliDateInput, todayISO, toJalali, fmtNum } from "../../utils/dateUtils";
import DismissibleHelp from "../../components/DismissibleHelp";
import DraftBar from "../../components/DraftBar";
import { useDraft } from "../../utils/draft";

const { Title, Text } = Typography;

const STAGES = [
  { value: "cut", label: "برش" },
  { value: "sewing", label: "دوخت" },
  { value: "final", label: "کنترل نهایی" },
];
const WASTE_TYPES = ["پارگی", "لکه", "اشتباه برش", "اشتباه دوخت", "نقص پارچه", "سایر"]
  .map((v) => ({ value: v }));

import { useMonthScope } from "../../components/MonthScope";

export default function WasteRegisterPage() {
  const [bijacks, setBijacks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [code, setCode] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [sizeLabel, setSizeLabel] = useState(null);
  const [stage, setStage] = useState("cut");
  const [wasteType, setWasteType] = useState("");
  const [qty, setQty] = useState(null);
  const [reason, setReason] = useState("");
  const [empId, setEmpId] = useState(null);
  const [wDate, setWDate] = useState(todayISO());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getWasteEvents({ q: search.trim() || undefined });
      setRows(r.data || []);
    } catch {
      message.error("خطا در بارگذاری ضایعات");
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getCutBijacks().then((r) => setBijacks(r.data || [])).catch(() => {});
    getEmployees().then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setEmployees(list.filter((e) => e.status !== "inactive"));
    }).catch(() => {});
  }, []);

  const group = useMemo(() => bijacks.find((g) => g.production_code === code) || null, [bijacks, code]);
  const productOpts = (group?.products || []).map((p) => ({
    value: p.order_id,
    label: [p.product_type, p.color].filter(Boolean).join(" | "),
  }));
  const sizeOpts = useMemo(() => {
    const p = (group?.products || []).find((x) => x.order_id === orderId);
    return (p?.sizes || []).map((s) => ({ value: s.size_label, label: s.size_label }));
  }, [group, orderId]);

  // v2.142.0 — پیش‌نویسِ خودکارِ فرمِ ضایعات
  const wasteDraftValue = useMemo(() => ({ code, orderId, sizeLabel, stage, wasteType, qty, reason, empId, wDate }),
    [code, orderId, sizeLabel, stage, wasteType, qty, reason, empId, wDate]);
  const wasteDraft = useDraft("waste:new", wasteDraftValue, {
    // فقط چیزی که واقعاً تایپ شده و ذخیره نشده (تعداد/علت)؛ بیجک/مرحلهٔ انتخاب‌شده بعد از ثبت هم می‌ماند
    isEmpty: (v) => !v || (!v.qty && !(v.reason || "").trim()),
  });
  const restoreWasteDraft = (v) => {
    if (!v) return;
    setCode(v.code ?? null); setOrderId(v.orderId ?? null); setSizeLabel(v.sizeLabel ?? null);
    setStage(v.stage || "cut"); setWasteType(v.wasteType || ""); setQty(v.qty ?? null);
    setReason(v.reason || ""); setEmpId(v.empId ?? null); setWDate(v.wDate || todayISO());
  };

  const submit = async () => {
    if (!code) return message.warning("کد بیجک / کد تولید را انتخاب کنید");
    if (!qty || qty <= 0) return message.warning("تعداد ضایعات را وارد کنید");
    try {
      await createWasteEvent({
        production_code: code, order_id: orderId || null, stage,
        waste_type: wasteType || null, size_label: sizeLabel || null,
        quantity: qty, reason: reason || null, employee_id: empId || null,
        event_date: wDate,
      });
      message.success("ضایعات ثبت شد");
      setQty(null); setReason(""); wasteDraft.clear();
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || "ثبت ضایعات ناموفق بود");
    }
  };

  const cols = [
    { title: "کد تولید", dataIndex: "production_code", width: 150,
      render: (v) => <Tag color="blue">{v || "—"}</Tag> },
    { title: "شماره بیجک", dataIndex: "bijack_no", width: 120, render: (v) => v || "—" },
    { title: "محصول", dataIndex: "product_type", width: 160, render: (v) => v || "—" },
    { title: "رنگ", dataIndex: "color", width: 90, render: (v) => v || "—" },
    { title: "سایز", dataIndex: "size_label", width: 80, render: (v) => v || "—" },
    { title: "مرحله", dataIndex: "stage", width: 100,
      render: (v) => STAGES.find((s) => s.value === v)?.label || v },
    { title: "نوع ضایعات", dataIndex: "waste_type", width: 130, render: (v) => v || "—" },
    { title: "تعداد", dataIndex: "quantity", width: 80, align: "center",
      render: (v) => <b>{fmtNum(v)}</b> },
    { title: "توضیح", dataIndex: "reason", width: 200, render: (v) => v || "—" },
    { title: "پرسنل مرتبط", dataIndex: "employee_name", width: 150, render: (v) => v || "—" },
    { title: "تاریخ", dataIndex: "event_date", width: 110, render: (v) => toJalali(v) },
    { title: "ثبت‌کننده", dataIndex: "recorded_by", width: 130, render: (v) => v || "—" },
    { title: "", width: 55,
      render: (_, r) => (
        <Popconfirm title="این ردیف ضایعات حذف شود؟" okText="بله" cancelText="خیر"
          onConfirm={async () => {
            await deleteWasteEvent(r.id); message.success("حذف شد"); load();
          }}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ) },
  ];

  // دورهٔ ماهانه — فقط ضایعاتِ همان ماه (per user یادش می‌ماند)
  const monthScope = useMonthScope("waste-register");
  const scopedRows = rows.filter((r) => monthScope.inRange(r.event_date));
  const total = scopedRows.reduce((a, r) => a + (r.quantity || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <Title level={5} style={{ margin: 0 }}>
          ثبت ضایعات <Text type="secondary" style={{ fontSize: 12 }}>(مستقل از مراحل، فقط برای مغایرت‌گیری)</Text>
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>تازه‌سازی</Button>
      </div>

      <DismissibleHelp id="waste-register" message="ضایعات روی حقوق و کارکرد اثر ندارد"
        description="این ثبت فقط برای مغایرت‌گیری تعداد برش با کنترل اول و کنترل نهایی است." />

      <DraftBar draft={wasteDraft} what="فرمِ ضایعات" onRestore={restoreWasteDraft} />
      <Card size="small" title="ثبت ردیف ضایعات" style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} md={5}>
            <Text type="secondary" style={{ fontSize: 12 }}>کد بیجک / کد تولید</Text>
            <Select showSearch allowClear value={code} style={{ width: "100%" }}
              placeholder="انتخاب کد تولید" optionFilterProp="label"
              onChange={(v) => { setCode(v); setOrderId(null); setSizeLabel(null); }}
              options={bijacks.map((g) => ({
                value: g.production_code,
                label: `${g.production_code}${g.bijack_no ? ` — بیجک ${g.bijack_no}` : ""}`,
              }))} />
          </Col>
          <Col xs={24} md={5}>
            <Text type="secondary" style={{ fontSize: 12 }}>محصول / رنگ</Text>
            <Select allowClear value={orderId} options={productOpts} style={{ width: "100%" }}
              placeholder="محصول" onChange={(v) => { setOrderId(v); setSizeLabel(null); }} />
          </Col>
          <Col xs={12} md={3}>
            <Text type="secondary" style={{ fontSize: 12 }}>سایز</Text>
            <Select allowClear value={sizeLabel} options={sizeOpts} style={{ width: "100%" }}
              placeholder="سایز" onChange={setSizeLabel} />
          </Col>
          <Col xs={12} md={3}>
            <Text type="secondary" style={{ fontSize: 12 }}>مرحله</Text>
            <Select value={stage} options={STAGES} style={{ width: "100%" }} onChange={setStage} />
          </Col>
          <Col xs={12} md={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>نوع ضایعات</Text>
            <AutoComplete value={wasteType} options={WASTE_TYPES} style={{ width: "100%" }}
              placeholder="مثلاً پارگی" onChange={setWasteType}
              filterOption={(input, opt) => opt.value.includes(input)} />
          </Col>
          <Col xs={12} md={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>تعداد ضایعات</Text>
            <InputNumber min={1} value={qty} onChange={setQty} style={{ width: "100%" }} />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>پرسنل مرتبط (اختیاری)</Text>
            <Select showSearch allowClear value={empId} onChange={setEmpId} style={{ width: "100%" }}
              optionFilterProp="label" placeholder="پرسنل"
              options={employees.map((e) => ({ value: e.id, label: `${e.full_name} (${e.employee_code})` }))} />
          </Col>
          <Col xs={12} md={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>تاریخ</Text>
            <div><JalaliDateInput value={wDate} onChange={setWDate} style={{ width: "100%" }} /></div>
          </Col>
          <Col xs={24} md={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>توضیح</Text>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="توضیح (اختیاری)" />
          </Col>
          <Col xs={24} md={6}>
            <Button type="primary" icon={<PlusOutlined />} onClick={submit} style={{ width: "100%" }}>
              ثبت ضایعات
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="ردیف‌های ضایعات" value={scopedRows.length} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="جمع تعداد ضایعات" value={total} /></Card></Col>
      </Row>

      <Card size="small" title="ضایعات ثبت‌شده"
        extra={
          <Space wrap>
            {monthScope.node}
            <Input.Search allowClear placeholder="جستجو: کد تولید / بیجک / محصول"
              onSearch={load} onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
          </Space>
        }>
        <Table size="small" rowKey="id" loading={loading} dataSource={scopedRows} columns={cols}
          scroll={{ x: "max-content" }} pagination={{ defaultPageSize: 15, showSizeChanger: true }} />
      </Card>
    </div>
  );
}
